import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { ChangeEvent, ReactElement, useMemo, useState } from "react";
import Plus from "src/assets/icons-redesign/plus.svg?react";
import { Attribute } from "src/components/criteria/criteria-settings";
import { Button } from "src/components/ui/button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { useAttributeList } from "src/hooks/useAttributeList";
import { SurveyAudienceCriteria } from "src/redux/survey/survey.types";
import { Input } from "../../../components/ui/input";
import { useIsMobile } from "../../../hooks/useMobile";

interface AddFilterButtonProps {
  readonly availableDmas?: Attribute;
  readonly surveyTypeAttributes?: Attribute[];
  readonly initialFilter?: SurveyAudienceCriteria;
  readonly filter?: SurveyAudienceCriteria;
  readonly setFilter: (newFilter: SurveyAudienceCriteria) => void;
  /** Only show attributes whose value is in this list (e.g. city, region, user_type) */
  readonly allowedAttributes?: string[];
}

const AddFilterButton = ({ availableDmas, surveyTypeAttributes, initialFilter, filter, setFilter, allowedAttributes }: AddFilterButtonProps) => {
  const { isLoading, attributes } = useAttributeList(initialFilter, availableDmas);
  const [checked, setChecked] = useState<SurveyAudienceCriteria>({});
  const [locationSearch, setLocationSearch] = useState('');
  const { isMdOrSmaller } = useIsMobile();

  const nonFilteredAttributes = useMemo(() => {
    if (surveyTypeAttributes?.length) {
      let list = surveyTypeAttributes.filter((attribute) => {
        const attName = attribute.value as keyof SurveyAudienceCriteria;
        return filter?.[attName] === undefined;
      });
      if (allowedAttributes?.length) {
        const allowedSet = new Set(allowedAttributes);
        list = list.filter((attr) => allowedSet.has(attr.value));
      }
      return list;
    }

    if (!attributes || isLoading) {
      return null;
    }

    let list = attributes.filter((attribute) => {
      const attName = attribute.value as keyof SurveyAudienceCriteria;
      return filter?.[attName] === undefined;
    });

    if (allowedAttributes?.length) {
      const allowedSet = new Set(allowedAttributes);
      list = list.filter((attr) => allowedSet.has(attr.value));
    }

    return list;
  }, [filter, attributes, isLoading, allowedAttributes, surveyTypeAttributes]);

  if (!nonFilteredAttributes || nonFilteredAttributes.length === 0) {
    return null;
  }

  const handleCheck = (criteria: string, name: string) => {
    const crit = checked[criteria as keyof SurveyAudienceCriteria] ?? [];

    if (crit?.includes(name)) {
      setChecked({
        ...checked,
        [criteria]: crit.filter(item => item !== name),
      });
    } else {
      setChecked({
        ...checked,
        [criteria]: [...crit, name],
      });
    }
  };

  const updateFilter = () => {
    const tmpCheck: any = checked;
    if (tmpCheck['Location']) {
      tmpCheck['DMA'] = tmpCheck['Location'];
      delete tmpCheck['Location'];
    }

    setFilter({
      ...filter,
      ...tmpCheck,
    });
  };

  const handleNoApplyClose = (open: boolean) => {
    !open && setChecked({});
    !open && setLocationSearch('');
  };

  const onLocationFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocationSearch(event.target.value);
  };

  function renderAttribute(attribute: Attribute): ReactElement {
    const { label, value, options } = attribute;
    const isLocation = label === 'Location';
    const lowerCaseSearch = locationSearch?.toLowerCase() ?? '';
    const filteredOptions = isLocation && locationSearch ? options.filter(option => option.label?.toLowerCase().includes(lowerCaseSearch)) : options;

    return (
      <DropdownMenuSub key={value}>
        <DropdownMenuSubTrigger>
          <span>
            {label}
          </span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent
          className="max-h-96 max-w-72 micro:max-w-56 gap-2 pb-2"
          sideOffset={isMdOrSmaller ? -84 : 0}
        >
          {isLocation && (
            <div className="flex m-2">
              <Input
                value={locationSearch}
                onChange={onLocationFilterChange}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search"
              />
            </div>
          )}
          <div className={`${isLocation ? 'max-h-72' : 'max-h-80'} overflow-y-auto`}>
            {filteredOptions.map((option) => (
              <DropdownMenuCheckboxItem
                checked={checked[label as keyof SurveyAudienceCriteria]?.some((item) => item === option.value)}
                onCheckedChange={() => handleCheck(label, option.value)}
                key={option.value}
                onSelect={(event) => event.preventDefault()}
              >
                <span>
                  {option.label}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </div>
          <div className="sticky t-0 r-0 l-0">
            <DropdownMenuItem className="flex justify-center">
              <Button
                variant="outline"
                size="med"
                className="h-8 w-full mx-4"
                onClick={updateFilter}
                disabled={Boolean(!checked?.[label as keyof SurveyAudienceCriteria]?.length)}
              >
                Apply
              </Button>
            </DropdownMenuItem>
          </div>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu onOpenChange={handleNoApplyClose}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size="med"
          className="gap-2 px-2"
        >
          <div className="size-6">
            {/* idk why, but tailwind css isn't overriding index.css imports? size-4 is taking precedence until I hardcode style */}
            <Plus style={{ height: '100%', width: '100%' }} />
          </div>
          <span>
            Add Filter
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom">
        <DropdownMenuSub>
          {nonFilteredAttributes.map(renderAttribute)}
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddFilterButton;
